import { CoreApp, DataSourceApi, DataSourceInstanceSettings } from '@grafana/data';
import { DataQuery, DataSourceJsonData } from '@grafana/schema';
import { ComponentSize } from '@grafana/ui';
import { createAssistantContextItem } from '../../context';
import { useAssistant } from '../../hook';
import { OpenAssistantButtonView } from './OpenAssistantButton';
import React from 'react';

function queryIsEmpty(query: DataQuery): boolean {
  const dataQueryProps = ['refId', 'hide', 'key', 'queryType', 'datasource'];

  for (const key in query) {
    if (key === 'label') {
      continue;
    }
    if (!dataQueryProps.includes(key)) {
      return false;
    }
  }

  return true;
}

export interface QueryWithAssistantButtonProps<TQuery extends DataQuery = DataQuery> {
  size?: ComponentSize;
  currentQuery: TQuery;
  queries: TQuery[];
  dataSourceInstanceSettings: DataSourceInstanceSettings;
  app?: CoreApp;
  datasourceApi: DataSourceApi<TQuery, DataSourceJsonData, {}> | null;
  title?: string;
}

export function QueryWithAssistantButton<TQuery extends DataQuery = DataQuery>({
  size,
  currentQuery,
  queries,
  dataSourceInstanceSettings,
  app,
  datasourceApi,
  title = 'Query with Assistant',
}: QueryWithAssistantButtonProps<TQuery>) {
  const { isAvailable, openAssistant } = useAssistant();
  if (!isAvailable || !openAssistant) {
    return null;
  }

  const origin = `grafana/query-editor/${dataSourceInstanceSettings.type}/${app ?? CoreApp.Unknown ?? 'unknown'}`;

  const hasCurrentQuery = !queryIsEmpty(currentQuery);
  const otherQueries = queries.filter((q) => q.refId !== currentQuery.refId && !queryIsEmpty(q));

  const context = [
    createAssistantContextItem('datasource', {
      datasourceUid: dataSourceInstanceSettings.uid,
    }),
  ];

  if (hasCurrentQuery) {
    context.push(
      createAssistantContextItem('structured', {
        title: 'Current query',
        data: currentQuery,
      })
    );
  }

  if (otherQueries.length > 0) {
    context.push(
      createAssistantContextItem('structured', {
        title: 'Other queries',
        data: {
          queries: otherQueries,
        },
      })
    );
  }

  const queryDisplayText =
    hasCurrentQuery && datasourceApi?.getQueryDisplayText ? datasourceApi.getQueryDisplayText(currentQuery) : null;

  const isUpdating = !!queryDisplayText;
  const actionText = isUpdating
    ? 'Help me update the current query to answer my questions and provide the insights I need.'
    : 'Help me create a new query to answer my questions and provide the insights I need.';

  const codeBlockLines: string[] = [];

  if (queryDisplayText) {
    codeBlockLines.push(`Current query: ${queryDisplayText}`);
  }

  codeBlockLines.push(`Selected data source: ${dataSourceInstanceSettings.name}`);


  const actionableSentence = isUpdating
    ? 'Please describe what you want to change or improve in this query.'
    : "Please describe what you want to query and what insights you're looking for.";

  const prompt = [actionText, '```', ...codeBlockLines, '```', actionableSentence].join('\n');

  return (
    <OpenAssistantButtonView
      prompt={prompt}
      origin={origin}
      context={context}
      autoSend={false}
      title={title}
      size={size}
      openAssistant={openAssistant}
    />
  );
}
